import { useDateTimeUtils } from 'civic-champs-shared/utils/useDateTimeUtils'
import { Opportunity, OpportunityVisibility } from '../interfaces'
import { OpportunityResponseWithRecurrenceInfo } from '../interfaces/interfaceCreateEditEvent'
import { EventQuestionSet } from './useGetEventQuestionSets'
import { pick, uniqueId } from 'lodash'
import { FormData, createEmptyFormRole, getInitialEditMode } from '../helpers/add-edit-helpers'
import { useCallback } from 'react'
import { DEFAULT_GEOFENCING } from '../../civic-champs-shared/constants/GEO_DATA'
import { RECURRING_TYPE } from '../../civic-champs-shared/core/utils/constants/RECURRING_TYPE_OPTIONS'
import { useFeatureEnabled } from '../../core/feature/hooks'
import { LocationAddressType, OrganizationLocationReporting } from '../../locations/types'
import {
  MONTHLY_RECURRING_TYPE,
  SIMPLE_RECURRING_TYPE,
} from '../../civic-champs-shared/core/utils/constants/SIMPLE_RECURRING_TYPE_OPTIONS'

const getDefaultFormData = ({ useTemplate = false }: { useTemplate?: boolean } = {}) => {
  const startTime = new Date()
  const endTime = new Date()
  startTime.setHours(12, 0, 0)
  endTime.setHours(13, 0, 0)

  return {
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    visibility: OpportunityVisibility.PUBLIC,
    groups: [],
    startDate: startTime,
    endDate: null,
    startTime,
    endTime,
    geofencing: DEFAULT_GEOFENCING,
    isTest: false,
    locationDetails: '',
    locationIsAddress: true,
    opportunityTemplate: null,
    isSchedulable: true,
    recurringType: RECURRING_TYPE.NEVER,
    roles: [createEmptyFormRole()],
    contactEmail: '',
    contactName: '',
    contactPhoneNumber: '',
    instructions: '',
    registrationMessage: '',
    followUpEnabled: false,
    followUpMessage: '',
    followUpQuestionnaireIds: [],
    questionnaires: [],
    waivers: [],
    organizationLocationId: null,
    useTemplate,
    useCustomAddress: false,
    customAddressType: LocationAddressType.AddressAndCoords,
    plusCode: '',
    dates: [],
    simpleRecurringType: SIMPLE_RECURRING_TYPE.SELECTED_DATES,
    monthlyRecurringType: MONTHLY_RECURRING_TYPE.RESPECT_DATE,
    useCustomPublishTime: false,
    allowMultipleRegistrations: false,
  } as FormData
}

export const useGetFormDataFromEvent = ({ useTemplate = false }: { useTemplate?: boolean } = {}) => {
  const { fakeAsLocalTimeForUI } = useDateTimeUtils()
  const isCalendarEventSwitchEnabled = useFeatureEnabled('NewAddEventUICalendarEvent')
  return useCallback(
    ({
      event,
      questionSets,
      isQuestionnaireAsSurveyEnabled,
      namedLocations,
    }: {
      event?: Opportunity | OpportunityResponseWithRecurrenceInfo
      questionSets: EventQuestionSet[]
      isQuestionnaireAsSurveyEnabled: boolean
      namedLocations: OrganizationLocationReporting[]
    }): FormData => {
      const defaultFormData = getDefaultFormData({ useTemplate })
      const eventFormData = pick(event || {}, Object.keys(defaultFormData))
      const dates = event
        ? {
            startDate: fakeAsLocalTimeForUI(event.startsAt).toDate(),
            startTime: fakeAsLocalTimeForUI(event.startsAt).toDate(),
            endDate: event.endsAt ? fakeAsLocalTimeForUI(event.endsAt).toDate() : undefined,
            endTime: event.endsAt ? fakeAsLocalTimeForUI(event.endsAt).toDate() : undefined,
            dates: [],
          }
        : {}
      let schedulableFormData: Partial<FormData> = {
        isSchedulable: isCalendarEventSwitchEnabled,
      }
      if (event && (event as OpportunityResponseWithRecurrenceInfo)?.schedulable) {
        const schedulableEvent = event as OpportunityResponseWithRecurrenceInfo
        schedulableFormData = {
          recurringType: schedulableEvent.recurrenceInfo.type,
          recurrencePattern: schedulableEvent.recurrenceInfo.pattern,
          isRecurring: schedulableEvent.is_recurring,
          roles: schedulableEvent.timeshifts?.flatMap(({ roles }) =>
            roles.map(({ id, available, all }) => ({
              roleId: id,
              registrations: all - available,
              quantity: all,
              itemId: uniqueId('role_'),
            })),
          ),
          timeShiftId: schedulableEvent.timeshifts?.[0].id,
          timeShiftName: schedulableEvent.timeshifts?.[0].name,
          contactEmail: schedulableEvent.contact_email,
          contactName: schedulableEvent.contact_name,
          contactPhoneNumber: schedulableEvent.contact_phone_number,
          instructions: schedulableEvent.instructions,
          registrationMessage: schedulableEvent.registration_message || '',
          followUpEnabled: !!schedulableEvent.follow_up_enabled,
          followUpMessage: schedulableEvent.follow_up_message || '',
          followUpQuestionnaireIds: schedulableEvent.follow_up_questionnaire_ids || [],
          questionnaires: questionSets.map((eventQuestionSet: any) => ({
            id: isQuestionnaireAsSurveyEnabled ? eventQuestionSet.survey?.id : eventQuestionSet.questionSet.id,
            name: eventQuestionSet.questionSet.name,
          })),
          waivers: schedulableEvent.requiredCredentials?.map(({ id, title }) => ({
            id,
            name: title,
          })),
          isSchedulable: true,
          editMode: getInitialEditMode(schedulableEvent),
          pointOfContactPersons: schedulableEvent.pointOfContactPersons,
          useCustomPublishTime: false,
          allowMultipleRegistrations: schedulableEvent.allowMultipleRegistrations ?? false,
        }

        if (schedulableEvent.forceVisibleAt) {
          const publishAt = fakeAsLocalTimeForUI(schedulableEvent.forceVisibleAt)
          Object.assign(schedulableFormData, {
            useCustomPublishTime: true,
            publishDate: publishAt.toDate(),
            publishTime: publishAt.toDate(),
          })
        }
      }
      if (!schedulableFormData.isSchedulable) {
        schedulableFormData.roles = []
      }
      const organizationLocationId = (event as OpportunityResponseWithRecurrenceInfo)?.locationId || null
      const customAddressType =
        event?.locationAddressType ??
        (event?.locationDetails
          ? event?.locationIsAddress
            ? LocationAddressType.PlusCode
            : LocationAddressType.CustomTextOnly
          : LocationAddressType.AddressAndCoords)
      const isPlusCode = customAddressType === LocationAddressType.PlusCode
      return {
        ...defaultFormData,
        ...eventFormData,
        ...schedulableFormData,
        ...dates,
        locationIsAddress: event?.locationIsAddress ?? !event?.locationDetails,
        organizationLocationId,
        useCustomAddress: !namedLocations.some(namedLocation => namedLocation.id === organizationLocationId),
        customAddressType,
        plusCode: isPlusCode ? event?.address || '' : '',
        address: isPlusCode ? '' : event?.address || '',
        city: isPlusCode ? '' : event?.city || '',
        state: isPlusCode ? '' : event?.state || '',
        zip: isPlusCode ? '' : event?.zip || '',
        locationAddressType: event?.locationAddressType,
      } as FormData
    },
    [fakeAsLocalTimeForUI, isCalendarEventSwitchEnabled, useTemplate],
  )
}
